// Support functions to prepare strings to match chrono's expected input
// See test suite for expected behavior
import { clamp } from 'lodash-es';
import { padNumber } from 'utils/numbers';
import type { DSTTransitionDetails } from './date-helper';

export function guessTimestampFromText(unparsedInput: string) {
  const input = unparsedInput.replace('.', ':');
  const numberInput = Number(input);
  const firstCharAsNumber = Number(input.charAt(0));

  if (input.length === 1) {
    return `${input}pm`;
  }

  if (input.length === 2 && firstCharAsNumber === 0) {
    return `${input.substring(1, 2)}am`;
  }

  if (input.length === 2 && numberInput < 24) {
    return `${input}:00`;
  }

  const onlyDigits = !input.match(/[a-z]+/i);
  if (input.length === 3 && onlyDigits) {
    return `${input.substring(0, 1)}:${input.substring(1, 3)}pm`;
  }

  if (input.length === 4 && input.includes(':')) {
    return `${input.substring(0, 1)}:${input.substring(2, 4)}pm`;
  }

  if (input.length === 4 && !input.includes(':') && onlyDigits) {
    return `${input.substring(0, 2)}:${input.substring(2, 4)}`;
  }

  return input;
}

export function parseTime(value: string): null | { hour: number; minute: number } {
  const parsed = value.match(/(\d+):?(\d+)?( ?am| ?pm)?/i);
  if (!parsed) {
    return null;
  }
  const [_1, hourString, minuteString, amPm] = parsed;
  let hour = Number(hourString);
  let minute = Number(minuteString ?? 0);

  if (hour === 12) {
    if (amPm?.trim().toLowerCase() === 'am') {
      hour = 0;
    }
  } else if (amPm?.trim().toLowerCase() === 'pm') {
    hour += 12;
  }
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  hour = clamp(hour, 0, 24);
  minute = clamp(minute, 0, 59);
  if (hour === 24) {
    minute = 0;
  }
  return { hour, minute };
}

interface SecondsToTimeFormatOptions {
  timeFormat?: 12 | 24;
  DSTTransitionDetails?: DSTTransitionDetails;
}

export function secondsToTimeFormat(
  seconds: number,
  { timeFormat = 24, DSTTransitionDetails }: SecondsToTimeFormatOptions = {}
) {
  const secondsInDay = 3600 * 24;
  const hours = Math.floor(seconds / 3600);

  let adjustedSeconds = seconds;
  let adjustedHours = hours;
  if (DSTTransitionDetails?.isDSTTransition) {
    const { transitionHour, offsetChangeHours } = DSTTransitionDetails;
    const transitionHourAsSeconds = transitionHour * 3600;
    if (seconds >= transitionHourAsSeconds) {
      adjustedSeconds = seconds + offsetChangeHours * 3600;
      adjustedHours = hours + offsetChangeHours;
    }
  }

  const isNextDay = adjustedSeconds > secondsInDay;
  const isPreviousDay = adjustedSeconds < 0;

  const displayHours = isNextDay
    ? adjustedHours - 24
    : isPreviousDay
      ? adjustedHours + 24
      : adjustedHours;
  const displayMinutes = Math.floor((adjustedSeconds - adjustedHours * 3600) / 60);

  if (timeFormat === 12) {
    const isAm = displayHours < 12 || displayHours >= 24;
    let amPmHours = displayHours % 12;
    amPmHours = amPmHours === 0 ? 12 : amPmHours;
    const postfix = isAm ? 'am' : 'pm';
    return `${amPmHours}:${padNumber(displayMinutes, 2)}${postfix}`;
  }

  // Normalize 24-hour format to use 00-23 range
  const normalizedHours = displayHours % 24;
  return `${padNumber(normalizedHours, 2)}:${padNumber(displayMinutes, 2)}`;
}

type PixelsToTimeFormatOptions = SecondsToTimeFormatOptions & {
  pixelsPerMinute: number;
};

export function pixelsToTimeFormat(pixels: number, options: PixelsToTimeFormatOptions) {
  const { pixelsPerMinute } = options;
  const seconds = (pixels / pixelsPerMinute) * 60;
  return secondsToTimeFormat(seconds, options);
}

export function removeAmPmSuffix(label: string, timeFormat: 12 | 24): string {
  if (timeFormat === 12) {
    return label.substring(0, label.length - 2);
  } else {
    return label;
  }
}
